import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef } from 'react';
import { useTheme } from 'styled-components';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import Icons from '../../Elements/Icons';
import Typography from '../../Typography';
import { DropAreaCard, UploadButton, DropAreaTarget, DropDescription } from './DropArea.styled';
import { fileUploadPrompt } from './fileUploadPrompt';
const DEFAULT_DROP_AREA_MIN_HEIGHT = 278;
const isDnDSuitableDevice = () => {
    // https://caniuse.com/mdn-api_navigatoruadata_mobile
    // @ts-expect-error experimental api
    const isMobile = navigator.userAgentData?.mobile;
    if (typeof isMobile === 'boolean') {
        return !isMobile;
    }
    // Vertical screens most likely not good for drag and drop
    return window.screen.width > window.screen.height;
};
function addDropEffect(el, cb, multiple) {
    el.ondragover = (ev) => {
        ev.preventDefault();
        if (ev.dataTransfer) {
            ev.dataTransfer.effectAllowed = 'all';
            ev.dataTransfer.dropEffect = 'copy';
            el.dataset.dragover = 'true';
        }
    };
    el.ondragleave = () => {
        el.dataset.dragover = 'false';
    };
    el.ondrop = async (ev) => {
        ev.preventDefault();
        if (ev.dataTransfer?.files) {
            const files = Array.from(ev.dataTransfer.files);
            if (multiple) {
                // Process all files when multiple is enabled
                for (const file of files) {
                    cb(file);
                }
            }
            else if (files.length > 0) {
                // Process only the first file when multiple is disabled
                cb(files[0]);
            }
        }
        el.dataset.dragover = 'false';
    };
}
function addClickEffect(el, cb, disabled, multiple) {
    if (disabled)
        return;
    el.onclick = (e) => {
        // Check if click originated from a label element (which wraps the file input)
        if (e.target && e.target instanceof HTMLLabelElement) {
            return;
        }
        // Check if click originated from within a label element
        if (e.target && e.target instanceof HTMLElement && e.target.closest('label')) {
            return;
        }
        fileUploadPrompt((files) => files.forEach(cb), multiple);
    };
}
const DropAreaCardSVG = forwardRef(function DropAreaCardSVG({ children, disabled, minHeight }, ref) {
    return (_jsxs(DropAreaTarget, { ref: ref, disabled: disabled, "data-testid": 'drop-area-target', "$minHeight": minHeight, children: [_jsx("svg", { xmlns: 'http://www.w3.org/2000/svg', children: _jsx("rect", { width: '100%', height: '100%', rx: '8', ry: '8', strokeWidth: '2', strokeDasharray: '10, 12', strokeDashoffset: '0', strokeLinecap: 'round' }) }), children] }));
});
export const DropArea = ({ disabled, title, subtitle, input, children, onFileAdd, multiple, minHeight: minHeightProp = DEFAULT_DROP_AREA_MIN_HEIGHT, }) => {
    const theme = useTheme();
    const areaRef = useCallbackRef((el) => {
        if (el) {
            addClickEffect(el, onFileAdd, disabled, multiple);
            if (isDnDSuitableDevice()) {
                addDropEffect(el, onFileAdd, multiple);
            }
            addClickEffect(el, onFileAdd, disabled, multiple);
        }
    });
    const minHeight = typeof minHeightProp === 'number' ? `${minHeightProp}px` : minHeightProp;
    return (_jsxs(DropAreaCard, { "data-testid": 'drop-area', children: [_jsxs(DropAreaCardSVG, { disabled: disabled, ref: areaRef, minHeight: minHeight, children: [_jsx(UploadButton, { disabled: disabled, "data-testid": 'upload-button', children: _jsx(Icons.Import, { color: disabled ? theme.colors.grey70_1 : theme.colors.mainDark }) }), _jsxs(DropDescription, { children: [_jsx(Typography.TextLarge, { "data-testid": 'upload-text', children: _jsxs(_Fragment, { children: [title, " ", input] }) }), subtitle && _jsx(Typography.TextMedium, { children: subtitle })] })] }), children] }));
};
